import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useGame } from "./GameContext";
import { LiveGameTime, formatGameDuration, formatNumber } from "../components/GameUi";
import { isDiplomacyUnlocked } from "../game-engine/diplomacy";

const navigation = [
  ["overview", "总览", "OVR"],
  ["star-map", "星图", "MAP"],
  ["resources", "资源", "RES"],
  ["technology", "科技", "TEC"],
  ["fleet", "舰队", "FLT"],
  ["diplomacy", "外交", "DIP"],
  ["events", "事件", "LOG"],
  ["settings", "设置", "SET"],
] as const;

export function GameLayout() {
  const auth = useAuth();
  const game = useGame();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const base = `/play/${game.saveId}`;
  const availableNavigation = navigation.filter(([path]) => {
    if (path === "diplomacy") return Boolean(game.state && isDiplomacyUnlocked(game.state));
    if (path === "events") return Boolean(game.state && (
      game.state.events.pending.length || game.state.events.log.length || game.state.events.reports.length
    ));
    return true;
  });

  useEffect(() => {
    const settings = game.state?.player.settings;
    if (!settings) return;
    document.documentElement.dataset.density = settings.density;
    document.documentElement.dataset.reducedMotion = String(settings.reducedMotion);
  }, [game.state?.player.settings]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  async function signOut() {
    setLogoutError(null);
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (cause) {
      setLogoutError(cause instanceof Error ? cause.message : "退出失败。");
    }
  }

  return (
    <div className="game-shell">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <ReturnReport />
      <header className="mobile-topbar">
        <span className="brand-mark">SC</span>
        <div><b>STELLAR COLONY</b><span>{game.state?.player.civilizationName ?? "同步中"} · <LiveGameTime compact /></span></div>
        <button className="mobile-logout" aria-label="返回存档舱" onClick={() => navigate("/game")}><span className="online-dot" aria-hidden="true" />存档</button>
      </header>
      <aside className="side-nav">
        <div className="brand">
          <span className="brand-mark">SC</span>
          <div><b>STELLAR COLONY</b><span>DEEP TIME COMMAND</span></div>
        </div>
        <div className="nav-clock">
          <span>当前游戏时间</span>
          <strong><LiveGameTime /></strong>
        </div>
        <nav aria-label="主要导航">
          {availableNavigation.map(([path, label, code]) => (
            <NavLink key={path} to={`${base}/${path}`} className={({ isActive }) => isActive ? "active" : undefined}>
              <span aria-hidden="true">{code}</span><b>{label}</b>
            </NavLink>
          ))}
        </nav>
        <div className="nav-account">
          <span>{auth.user?.displayName}</span>
          <small>{auth.user?.email}</small>
          <button className="button ghost" onClick={() => navigate("/game")}>返回存档舱</button>
          <button className="button ghost" onClick={() => void signOut()}>退出登录</button>
          {logoutError && <small className="danger-text" role="alert">{logoutError}</small>}
        </div>
      </aside>

      <main id="main-content" className="game-main" tabIndex={-1}>
        <Feedback />
        {game.status === "loading" && !game.state ? (
          <div className="loading-panel" role="status"><span className="loading-bar" />正在同步文明进度并载入权威存档…</div>
        ) : game.status === "error" && !game.state ? (
          <div className="error-panel" role="alert">
            <h1>存档载入失败</h1>
            <p>{game.error}</p>
            <button className="button primary" onClick={() => void game.refresh()}>重新载入</button>
          </div>
        ) : <Outlet />}
      </main>

      <aside className="summary-rail" aria-label="即时摘要">
        <div>
          <span className="rail-label">当前游戏时间</span>
          <strong><LiveGameTime /></strong>
          <small>状态版本 {game.stateVersion}</small>
        </div>
        <div className="rail-resource tone-energy">
          <span className="rail-label">各星球能源合计</span>
          <strong>{formatNumber(game.state?.resources.energy.available ?? 0)}</strong>
          <small>年产 +{formatNumber(game.state?.resources.energy.productionPerYear ?? 0)}</small>
        </div>
        <div className="rail-resource tone-materials">
          <span className="rail-label">各星球材料合计</span>
          <strong>{formatNumber(game.state ? Object.values(game.state.resources.materials).reduce((sum, value) => sum + value, 0) : 0)}</strong>
          <small>建设时按所在地库存扣除</small>
        </div>
        <div className="rail-resource tone-research">
          <span className="rail-label">科研点</span>
          <strong>{formatNumber(game.state?.resources.research ?? 0)}</strong>
          <small>年产 +{formatNumber(game.state?.resources.researchPerYear ?? 0)}</small>
        </div>
        <div>
          <span className="rail-label">进行中</span>
          <strong>{game.state?.missions.filter((mission) => mission.status === "enRoute").length ?? 0} 项任务</strong>
          <small>{game.state?.events.pending.length ?? 0} 项待决事件</small>
        </div>
      </aside>

      <nav className="bottom-nav" aria-label="移动端主要导航">
        {availableNavigation.map(([path, label, code]) => (
          <NavLink key={path} to={`${base}/${path}`} className={({ isActive }) => isActive ? "active" : undefined}>
            <span aria-hidden="true">{code}</span><b>{label}</b>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function ReturnReport() {
  const game = useGame();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const report = game.offline;
  const shouldShow = Boolean(report && (report.elapsedRealSeconds >= 60 || report.messages.length > 0));

  useEffect(() => {
    const dialog = dialogRef.current;
    if (shouldShow && dialog && !dialog.open) dialog.showModal();
  }, [shouldShow]);

  if (!report || !shouldShow) return null;
  const materialDelta = Object.values(report.resourceDelta.materials).reduce((sum, value) => sum + (value ?? 0), 0);
  const messages = report.messages.slice(-6);
  return (
    <dialog ref={dialogRef} className="return-dialog" aria-labelledby="return-report-heading" onClose={game.dismissOffline}>
      <form method="dialog">
        <header>
          <div><p className="eyebrow">WELCOME BACK</p><h2 id="return-report-heading">挂机期间的文明报告</h2></div>
          <span className="status-chip">推进 {formatGameDuration(report.elapsedGameYears)}</span>
        </header>
        <p>你离开了约 {formatElapsed(report.elapsedRealSeconds)}。以下是库存净变化，已包含生产、维护、贸易和满仓影响。</p>
        <div className="return-resource-grid" aria-label="挂机期间资源净变化">
          <span className="tone-energy"><small>可用能源</small><b>{signed(report.resourceDelta.energy)}</b></span>
          <span className="tone-materials"><small>工程材料</small><b>{signed(materialDelta)}</b></span>
          <span className="tone-research"><small>科研点</small><b>{signed(report.resourceDelta.research)}</b></span>
        </div>
        {messages.length > 0 && (
          <div className="return-events"><b>期间发生</b><ul>{messages.map((message, index) => <li key={`${message}-${index}`}>{message}</li>)}</ul></div>
        )}
        <button className="button primary full" value="acknowledged">查看当前文明</button>
      </form>
    </dialog>
  );
}

function signed(value: number) {
  return `${value >= 0 ? "+" : ""}${formatNumber(value)}`;
}

function formatElapsed(seconds: number) {
  if (seconds < 3_600) return `${formatNumber(seconds / 60, 1)} 分钟`;
  if (seconds < 86_400) return `${formatNumber(seconds / 3_600, 1)} 小时`;
  return `${formatNumber(seconds / 86_400, 1)} 天`;
}

function Feedback() {
  const game = useGame();
  if (game.pendingAction) {
    return (
      <div className="feedback-banner warning" role="alert">
        <span><b>操作结果未知</b> 网络中断，已保留同一个幂等键，安全重试不会重复执行。</span>
        <div>
          <button className="button compact" onClick={() => void game.retryPending()} disabled={game.busy}>使用原键重试</button>
          <button className="button compact ghost" onClick={game.dismissPending}>放弃重试</button>
        </div>
      </div>
    );
  }
  if (game.error) {
    return <div className="feedback-banner danger" role="alert"><span>{game.error}</span><button className="button compact ghost" onClick={game.clearFeedback}>关闭</button></div>;
  }
  if (game.notice.length) {
    return <div className="feedback-banner success" role="status"><span>{game.notice.join(" · ")}</span><button className="button compact ghost" onClick={game.clearFeedback}>关闭</button></div>;
  }
  return null;
}
